import clsx from 'clsx';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ArrowDownLeft, ArrowUpRight, Banknote, CalendarPlus, Coins, History, RotateCcw, Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatMoney, formatMonth, formatNumber } from '../lib/format';
import {
  calendarDate,
  dayOf,
  deltaText,
  isPenaltyRedemption,
  pointsText,
  reasonKey,
  timeOf,
  timesBundles,
  type Redemption,
  type RedemptionOption,
  type RedemptionType,
  type WalletOptions,
  type WalletTransaction,
} from '../lib/wallet';
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, LoadingBlock, PageHeader, Pagination, type BadgeTone } from '../components/ui';

const ICONS: Record<RedemptionType, typeof Coins> = {
  REMOVE_QUARTER_DAY_PENALTY: Undo2,
  REMOVE_HALF_DAY_PENALTY: Undo2,
  REMOVE_FULL_DAY_PENALTY: Undo2,
  ADD_ONE_LEAVE_DAY: CalendarPlus,
  CASH_REWARD: Banknote,
};

/**
 * My wallet (CR-WALLET-001), `/wallet`, from the menu under one's name — between
 * Android app and Change password (operator, 2026-10-20). The signed-in employee's
 * own points, the five rewards they buy with the price each has now, how it works
 * at those prices, and every movement of points, newest first. Points are only
 * spent here, each time confirmed on its own page; nothing here adds any.
 */
export function WalletPage() {
  const { t } = useI18n();
  const [page, setPage] = useState(1);

  const options = useQuery({ queryKey: ['wallet', 'options'], queryFn: () => api.get<WalletOptions>('/wallet/me/options') });
  const history = useQuery({
    queryKey: ['wallet', 'transactions', page],
    queryFn: () => api.get<Paginated<WalletTransaction> & { balance: number }>('/wallet/me/transactions', { page, pageSize: 15 }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader title={t('wallet.title')} subtitle={t('wallet.subtitle')} />
      {options.isLoading ? (
        <LoadingBlock />
      ) : options.error || !options.data ? (
        <ErrorState error={options.error} onRetry={() => void options.refetch()} />
      ) : (
        <>
          <BalanceCard balance={options.data.balance} />
          {/* The heading sits above both columns, as wide as the rewards, so Points
              history starts level with the first row of rewards (operator, 2026-10-21). */}
          <div className="mb-3 mt-6 xl:grid xl:grid-cols-3 xl:gap-6">
            <div className="xl:col-span-2">
              <h2 id="wallet-redeem" className="text-h2 text-[var(--text-primary)]">
                {t('wallet.redeemTitle')}
              </h2>
              <p className="mt-0.5 text-body-sm text-muted">{t('wallet.redeemSubtitle')}</p>
            </div>
          </div>
          <div className="grid gap-6 xl:grid-cols-3">
            <div className="min-w-0 space-y-6 xl:col-span-2">
              <section aria-labelledby="wallet-redeem" className="grid gap-4 sm:grid-cols-2">
                {options.data.options.map((option) => (
                  <RewardCard key={option.type} option={option} wide={option.type === 'CASH_REWARD'} />
                ))}
              </section>
              <HowItWorks options={options.data.options} />
            </div>
            <HistoryCard
              query={history}
              page={page}
              onPage={setPage}
            />
          </div>
        </>
      )}
    </>
  );
}

/** The balance, large — and on a zero balance, what it means without promising how points come. */
function BalanceCard({ balance }: { balance: number }) {
  const { t, locale } = useI18n();
  return (
    <Card className="relative overflow-hidden">
      {/* A hairline of the brand along the card's top edge. */}
      <span className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-primary/60 to-signal/70" aria-hidden />
      <div className="flex flex-wrap items-center gap-5">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-primary/12 text-primary-600 dark:text-primary-400" aria-hidden>
          <Coins className="h-7 w-7" />
        </span>
        <div className="min-w-0">
          <p className="text-overline uppercase text-[var(--text-tertiary)]">{t('wallet.balance')}</p>
          <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
            <span className="text-hero tabular text-[var(--text-primary)]">{formatNumber(balance, locale)}</span>
            <span className="text-title text-muted">{Math.abs(balance) === 1 ? t('wallet.pointUnit') : t('wallet.pointsUnit')}</span>
          </p>
        </div>
      </div>
      <p className="mt-4 max-w-3xl text-body-sm text-muted">{balance === 0 ? t('wallet.zeroBalance') : t('wallet.balanceHint')}</p>
    </Card>
  );
}

/** One reward: what it does, what it costs now, and Redeem — which only opens its confirmation. */
function RewardCard({ option, wide }: { option: RedemptionOption; wide: boolean }) {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const Icon = ICONS[option.type];
  const reason = reasonKey(option);
  const priced = option.points != null;
  const cash = option.cash;

  const description =
    option.type === 'ADD_ONE_LEAVE_DAY'
      ? t('wallet.desc.ADD_ONE_LEAVE_DAY', {
          type: option.leaveType ? localized(option.leaveType) : t('wallet.annualLeave'),
          year: option.year ?? '',
        })
      : t(`wallet.desc.${option.type}` as TranslationKey);

  const held = option.heldBack;
  const heldParts = held
    ? [
        held.awaitingDecision > 0 && t('wallet.held.awaitingDecision', { n: held.awaitingDecision }),
        held.dayNotOver > 0 && t('wallet.held.dayNotOver', { n: held.dayNotOver }),
        held.onPayroll > 0 && t('wallet.held.onPayroll', { n: held.onPayroll }),
      ].filter((part): part is string => !!part)
    : [];

  return (
    <article
      className={clsx('surface flex flex-col rounded-lg p-5', wide && 'sm:col-span-2')}
      aria-labelledby={`reward-${option.type}`}
    >
      <div className="flex items-start gap-3">
        <span
          className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-[var(--surface-sunken)] text-primary-600 dark:text-primary-400"
          aria-hidden
        >
          <Icon className="h-5 w-5 flip-rtl" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 id={`reward-${option.type}`} className="text-title text-[var(--text-primary)]">
            {t(`wallet.type.${option.type}` as TranslationKey)}
          </h3>
          <p className="mt-1 text-body-sm text-muted">{description}</p>
        </div>
      </div>

      <div className="mt-auto pt-4">
        <div className="flex flex-wrap items-end justify-between gap-3 border-t hairline pt-4">
          <div className="min-w-0">
            <p className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">
              {option.type === 'CASH_REWARD' ? t('wallet.rate') : t('wallet.price')}
            </p>
            {!priced ? (
              <p className="mt-0.5 text-body-sm text-muted">{t('wallet.notPriced')}</p>
            ) : cash ? (
              <p className="mt-0.5 text-h3 tabular text-[var(--text-primary)]">
                {t('wallet.cashRate', {
                  points: pointsText(cash.pointsPerBundle, locale, t),
                  amount: formatMoney(cash.amountPerBundle, cash.currency, locale),
                })}
              </p>
            ) : (
              <p className="mt-0.5 text-h3 tabular text-[var(--text-primary)]">{pointsText(option.points!, locale, t)}</p>
            )}
          </div>
          <Button
            variant="primary"
            disabled={!option.available}
            onClick={() => navigate(`/wallet/redeem/${option.type}`)}
            title={reason && reason !== 'wallet.reason.NOT_CONFIGURED' ? t(reason) : undefined}
          >
            {t('wallet.redeem')}
          </Button>
        </div>
        {/* Why it cannot be redeemed — not for an unpriced one: its price already says so. */}
        {!option.available && reason && reason !== 'wallet.reason.NOT_CONFIGURED' && (
          <p className="mt-3 text-caption text-warning-fg dark:text-warning-dark">
            {t(reason, { currency: cash?.currency ?? '' })}
          </p>
        )}
        {isPenaltyRedemption(option.type) && option.available && option.eligibleCount != null && (
          <p className="mt-3 text-caption text-muted">{t('wallet.eligibleCount', { n: option.eligibleCount })}</p>
        )}
        {heldParts.length > 0 && <p className="mt-1 text-caption text-faint">{heldParts.join(' · ')}</p>}
      </div>
    </article>
  );
}

/** "How the points wallet works" — the text, and each reward's price as it stands, never an invented one (§3.4). */
function HowItWorks({ options }: { options: RedemptionOption[] }) {
  const { t, locale } = useI18n();
  const cash = options.find((o) => o.type === 'CASH_REWARD')?.cash ?? null;

  return (
    <Card>
      <CardHeader title={t('wallet.howTitle')} />
      <p className="max-w-3xl text-body-sm leading-relaxed text-[var(--text-secondary)]">{t('wallet.howText')}</p>
      <dl className="mt-4 divide-y divide-[var(--line)] rounded-md border hairline">
        {options.map((option) => (
          <div key={option.type} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 py-2.5">
            <dt className="text-body-sm text-[var(--text-primary)]">{t(`wallet.type.${option.type}` as TranslationKey)}</dt>
            <dd className="text-body-sm tabular text-muted">
              {option.points == null
                ? t('wallet.howUnpriced')
                : option.cash
                  ? t('wallet.howCash', {
                      points: pointsText(option.cash.pointsPerBundle, locale, t),
                      amount: formatMoney(option.cash.amountPerBundle, option.cash.currency, locale),
                    })
                  : t('wallet.howNeeds', { points: pointsText(option.points, locale, t) })}
            </dd>
          </div>
        ))}
      </dl>
      {/* An example only when the settings make one: two bundles, worked out — not anyone's balance. */}
      {cash && (
        <p className="mt-3 text-caption text-muted">
          {t('wallet.howExample', {
            points: pointsText(cash.pointsPerBundle * 2, locale, t),
            amount: formatMoney(timesBundles(cash.amountPerBundle, 2), cash.currency, locale),
          })}
        </p>
      )}
    </Card>
  );
}

/** Every movement of points, newest first: how many, the day and the time, what for — never why points were added. */
function HistoryCard({
  query,
  page,
  onPage,
}: {
  query: UseQueryResult<Paginated<WalletTransaction> & { balance: number }>;
  page: number;
  onPage: (page: number) => void;
}) {
  const { t } = useI18n();
  const data = query.data;
  return (
    <Card padded={false} className="h-fit min-w-0">
      <CardHeader title={t('wallet.historyTitle')} className="p-5 pb-0" />
      {query.isLoading ? (
        <LoadingBlock />
      ) : query.error || !data ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : data.items.length === 0 ? (
        <EmptyState icon={<History className="h-8 w-8" />} title={t('wallet.historyEmpty')} hint="" />
      ) : (
        <>
          <ul className="mt-2 divide-y divide-[var(--line)] border-t hairline">
            {data.items.map((row) => (
              <HistoryRow key={row.id} row={row} />
            ))}
          </ul>
          <Pagination page={page} pageCount={data.pageCount} total={data.total} onChange={onPage} />
        </>
      )}
    </Card>
  );
}

function HistoryRow({ row }: { row: WalletTransaction }) {
  const { t, locale } = useI18n();
  const r = row.redemption;
  const reward = r ? t(`wallet.type.${r.type}` as TranslationKey) : '';
  const label =
    row.kind === 'CREDIT'
      ? t('wallet.kind.CREDIT')
      : row.kind === 'REFUND_CREDIT'
        ? t('wallet.kind.REFUND_CREDIT')
        : t('wallet.kind.REDEMPTION_DEBIT', { reward });
  const Icon = row.kind === 'CREDIT' ? ArrowDownLeft : row.kind === 'REFUND_CREDIT' ? RotateCcw : ArrowUpRight;
  const gain = row.deltaPoints > 0;

  return (
    <li className="flex items-start gap-3 px-5 py-3">
      <span
        className={clsx(
          'mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full',
          gain ? 'bg-success/12 text-success-fg dark:text-success' : 'bg-primary/12 text-primary-600 dark:text-primary-400',
        )}
        aria-hidden
      >
        <Icon className="h-4 w-4 flip-rtl" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-body-sm font-medium text-[var(--text-primary)]">{label}</p>
        <p className="mt-0.5 text-caption text-muted tabular">
          {t('wallet.date')}: {dayOf(row.occurredAt, locale)} · {t('wallet.time')}: {timeOf(row.occurredAt, locale)}
        </p>
        {r && row.kind === 'REFUND_CREDIT' && <p className="mt-0.5 text-caption text-muted">{reward}</p>}
        {r && row.kind === 'REDEMPTION_DEBIT' && <RedemptionDetail redemption={r} />}
      </div>
      <div className="shrink-0 text-end">
        <p
          className={clsx(
            'whitespace-nowrap text-body-sm font-semibold tabular',
            gain ? 'text-success-fg dark:text-success' : 'text-error-fg dark:text-error-dark',
          )}
          aria-label={deltaText(row.deltaPoints, locale, t)}
        >
          {/* The sign stays on the number's left in either language; the unit follows the reading order. */}
          <bdi dir="ltr">
            {gain ? '+' : '−'}
            {formatNumber(Math.abs(row.deltaPoints), locale)}
          </bdi>{' '}
          {Math.abs(row.deltaPoints) === 1 ? t('wallet.pointUnit') : t('wallet.pointsUnit')}
        </p>
        {r && row.kind === 'REDEMPTION_DEBIT' && <StatusBadge redemption={r} />}
      </div>
    </li>
  );
}

/** What a redemption bought: the penalty it cancelled, the day it added, or the cash and where it stands. */
function RedemptionDetail({ redemption: r }: { redemption: Redemption }) {
  const { t, locale } = useI18n();
  let line: ReactNode = null;
  if (r.penalty) {
    const detail = locale === 'ar' ? r.penalty.detailAr ?? r.penalty.detail : r.penalty.detail;
    line = (
      <>
        {t(`wallet.penaltyKind.${r.penalty.kind ?? 'OTHER'}` as TranslationKey)} · {calendarDate(r.penalty.date, locale)}
        {detail && <> · <bdi>{detail}</bdi></>}
      </>
    );
  } else if (r.leave) {
    line = t('wallet.leaveAdded', {
      type: locale === 'ar' ? r.leave.leaveType.nameAr || r.leave.leaveType.name : r.leave.leaveType.name,
      year: r.leave.year,
    });
  } else if (r.cash) {
    const amount = formatMoney(r.cash.amount, r.cash.currency, locale);
    const month = r.cash.payroll ? formatMonth(r.cash.payroll.year, r.cash.payroll.month, locale) : null;
    line =
      r.cash.status === 'PAID'
        ? r.cash.settledVia === 'PAYROLL' && month
          ? t('wallet.cashPaidWith', { amount, month })
          : t('wallet.cashPaid', { amount })
        : r.cash.status === 'CANCELLED'
          ? t('wallet.cashCancelled', { amount })
          : month
            ? t('wallet.cashOnPayroll', { amount, month })
            : t('wallet.cashPending', { amount });
  }
  return line ? <p className="mt-0.5 text-caption text-muted">{line}</p> : null;
}

function StatusBadge({ redemption: r }: { redemption: Redemption }) {
  const { t } = useI18n();
  const [tone, key]: [BadgeTone, TranslationKey] =
    r.status === 'CANCELLED'
      ? ['neutral', 'wallet.status.CANCELLED']
      : r.status === 'PENDING_SETTLEMENT'
        ? ['warning', 'wallet.status.PENDING_SETTLEMENT']
        : r.cash
          ? ['success', 'wallet.status.PAID']
          : ['success', 'wallet.status.COMPLETED'];
  return (
    <Badge tone={tone} className="mt-1">
      {t(key)}
    </Badge>
  );
}
